import { DSDropdown } from "@components/design-system/molecules";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "@hooks";
import { useEffect } from "react";

/**
 * Reusable component for selecting a user from a team's user list.
 *
 * Supports:
 * - Defaulting to current user when selection becomes invalid
 * - "(You)" suffix on the current user's name
 * - Optional label display
 * - Works with any team (not just currentTeam)
 */
export default function TeamUserSelect({
  team,
  value,
  onChange,
  showLabel = false,
  label = "Select user",
  placeholder = "Select user",
  searchPlaceholder = "Search team",
  width = "w-48",
  dataTestId,
  defaultToCurrentUser = false,
}) {
  const { userUuid } = useAuth();
  const users = team?.users || [];

  // Default to current user if selection becomes invalid and defaultToCurrentUser is true
  useEffect(() => {
    const teamUsers = team?.users || [];
    if (
      defaultToCurrentUser &&
      (!team?.uuid ||
        !teamUsers.length ||
        !teamUsers.filter((u) => u.uuid === value).length)
    ) {
      onChange?.(null, userUuid);
    }
  }, [team, value, onChange, userUuid, defaultToCurrentUser]);

  return (
    <DSDropdown
      options={users}
      value={value || userUuid}
      onChange={(val, option) => onChange?.(null, val, option)}
      getOptionLabel={(user) => {
        const name = `${user.firstName} ${user.lastName}`;
        return user.uuid === userUuid ? `${name} (You)` : name;
      }}
      getOptionValue={(user) => user.uuid}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      search
      leadingIcon={faUser}
      label={showLabel ? label : undefined}
      width={width}
      dataTestId={dataTestId}
      getOptionTestId={(user) =>
        `${dataTestId || "team-user"}-option-${user.uuid}`
      }
    />
  );
}
